// ===========================================
// "Family replied" (enquiries plan §7, Phase 2)
// ===========================================
// The family answered: the ball comes back to the school and the chase date clears. A Not
// Proceeding enquiry reopens to In Conversation (A10): the dialog says so before it happens.

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { Enquiry, FamilyRepliedData } from '../../../api/enquiry.api';
import { useEnquiryConfig, useFamilyReplied } from '../../../hooks/useEnquiries';
import { carriesBall, stageLabel } from './enquiryUi';

type Channel = NonNullable<FamilyRepliedData['channel']>;

export interface FamilyRepliedDialogProps {
  enquiry: Enquiry;
  open: boolean;
  onClose: () => void;
}

export default function FamilyRepliedDialog({ enquiry, open, onClose }: FamilyRepliedDialogProps) {
  const { data: config } = useEnquiryConfig();
  const replied = useFamilyReplied();
  const [channel, setChannel] = useState<Channel>('EMAIL');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setChannel('EMAIL');
    setNote('');
  }, [open]);

  const reopens = enquiry.stage === 'NOT_PROCEEDING';

  const handleSave = () => {
    replied.mutate(
      { id: enquiry.id, data: { channel, note: note.trim() ? note : null } },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog open={open} onClose={replied.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>The family replied</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {reopens && (
            <Alert severity="info">
              This enquiry is {stageLabel(config, 'NOT_PROCEEDING')}. Saving reopens it at{' '}
              {stageLabel(config, 'IN_CONVERSATION')}.
            </Alert>
          )}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={channel}
            onChange={(_, v: Channel | null) => v && setChannel(v)}
            aria-label="How they replied"
          >
            <ToggleButton value="EMAIL">Email</ToggleButton>
            <ToggleButton value="PHONE_CALL">Call</ToggleButton>
            <ToggleButton value="SMS">Text</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="What they said"
            placeholder="Optional"
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 10_000 }}
          />
          <Typography variant="caption" color="text.secondary">
            {/* An Enrolled, Spam or to-review enquiry carries no ball: the reply is recorded only (review L10). */}
            {reopens || carriesBall(enquiry.stage)
              ? "The next move is the school's again. "
              : 'It is recorded in the history; nothing else changes. '}
            Once saved, this cannot be changed or deleted by anyone.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={replied.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={replied.isPending}>
          {replied.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
